/**
 * 교육실적(program) 인라인 편집 유효성 스펙
 *
 * 원본: 대량등록 템플릿 "교육실적_예시_v2.0" 의 데이터 유효성(드롭다운/custom 수식) 규칙.
 * 강사(tutorEditSpec)와 동일하게 (value, row) => '' | '에러메시지' 형태로 통일한다.
 *
 * 엑셀과 다른 점 2가지:
 * - 날짜: 엑셀은 YYYYMMDD 8자리지만 목록 API 는 'YYYY-MM-DD' 로 내려주므로 하이픈 형식으로 검증한다.
 * - 소외여부: 엑셀은 Y/N 이지만 API 는 boolean 이므로 그리드에서는 체크박스로 다룬다.
 */

/* ------------------------------ 허용값(드롭다운) ------------------------------ */
// 카테고리1 = 14종(해당없음 불가), 카테고리2·3 = 해당없음 + 14종
const CATEGORY_BASE = [
    '컴퓨팅 시스템 - 피지컬컴퓨팅',
    '컴퓨팅 시스템 - 로보틱스',
    '알고리즘과 프로그래밍 - 언플러그드',
    '알고리즘과 프로그래밍 - 블록코딩',
    '알고리즘과 프로그래밍 - 텍스트코딩',
    '알고리즘과 프로그래밍 - 피지컬 코딩',
    '디지털 문화 - 메타버스',
    '디지털 문화 - VR/AR',
    '디지털 문화 - 디지털 문화',
    '인공지능 - 데이터분석',
    '데이터 - AI',
    '창의 융합 - 3D',
    '창의 융합 - 앱 개발',
    '창의 융합 - 메이커',
]

export const PROGRAM_OPTIONS = {
    educationType: ['센터교육', '찾아가는 교육', '기타'],
    course: ['정규과정', '비정규과정'],
    category1: CATEGORY_BASE,
    categoryEtc: ['해당없음', ...CATEGORY_BASE],
    registrationYear: ['2026', '2025', '2024', '2023', '2022', '2021', '2020', '2019'],
    organizationType: ['일반학교', '도서벽지', '특수학교', '지역아동센터', '기타'],
    grade: ['초등', '중등', '고등', '혼합'],
}

/* ------------------------------ 검증 프리미티브 ------------------------------ */
const isBlank = v => v === undefined || v === null || String(v).trim() === ''

// 엑셀 custom: AND(X<>"", LEN(X)>=2, ISERROR(VALUE(X))) — 2자 이상이고 순수 숫자가 아님
const isText2 = v => !isBlank(v) && String(v).trim().length >= 2 && Number.isNaN(Number(String(v).trim()))

// 엑셀 custom: AND(ISNUMBER(X), X>=0)
const isNonNegNumber = v => !isBlank(v) && !Number.isNaN(Number(v)) && Number(v) >= 0

// 목록 API 형식(YYYY-MM-DD) + 실제 존재하는 날짜. 엑셀의 연도 2000~2999 제한도 유지.
export const isYmdDash = v => {
    if (isBlank(v) || !/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return false
    const [y, m, d] = String(v).split('-').map(n => parseInt(n, 10))
    if (y < 2000 || y > 2999) return false
    const dt = new Date(y, m - 1, d)
    return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d
}

/* 대표번호·휴대전화·이메일 형식 검사기는 수요처 담당자 항목에서만 쓰였다.
   해당 항목의 유효성을 전부 없애면서 함께 제거했다. */

/** 선택 항목: 비어 있으면 통과, 값이 있을 때만 규칙 적용 */
const optional = (check, msg) => v => (isBlank(v) ? '' : check(v) ? '' : msg)
/** 필수 항목 */
const requiredBy = (check, msg) => v => (check(v) ? '' : msg)
/** 드롭다운 */
const inList = (list, msg, allowBlank = false) => v =>
    (allowBlank && isBlank(v)) || list.includes(v) ? '' : msg

/* ------------------------------ 필드 스펙 ------------------------------
 * label    : 사용자 표시 라벨
 * required : 필수 여부(엑셀 헤더의 * 기준)
 * editor   : 'select' | 'boolean' | 'text' | 'number'
 * options  : select 허용값
 * validate : (value, row) => '' | '에러메시지'
 */
export const PROGRAM_FIELD_SPEC = {
    /* --- 교육 기본정보 --- */
    educationType: {
        label: '유형',
        required: true,
        editor: 'select',
        options: PROGRAM_OPTIONS.educationType,
        validate: inList(PROGRAM_OPTIONS.educationType, '유형은 센터교육/찾아가는 교육/기타 중 선택하세요.'),
    },
    course: {
        label: '정규구분',
        required: true,
        editor: 'select',
        options: PROGRAM_OPTIONS.course,
        validate: inList(PROGRAM_OPTIONS.course, '정규구분은 정규과정/비정규과정 중 선택하세요.'),
    },
    programName: {
        label: '제목',
        required: true,
        editor: 'text',
        validate: requiredBy(isText2, '제목은 2자 이상이어야 하며 숫자만으로는 입력할 수 없습니다.'),
    },
    category1: {
        label: '카테고리1',
        required: true,
        editor: 'select',
        options: PROGRAM_OPTIONS.category1,
        validate: inList(PROGRAM_OPTIONS.category1, '카테고리1은 목록에서 선택하세요.'),
    },
    category2: {
        label: '카테고리2',
        editor: 'select',
        options: PROGRAM_OPTIONS.categoryEtc,
        validate: inList(PROGRAM_OPTIONS.categoryEtc, '카테고리2는 목록에서 선택하세요.', true),
    },
    category3: {
        label: '카테고리3',
        editor: 'select',
        options: PROGRAM_OPTIONS.categoryEtc,
        validate: inList(PROGRAM_OPTIONS.categoryEtc, '카테고리3은 목록에서 선택하세요.', true),
    },

    /* --- 프로그램 운영정보 --- */
    classHours: {
        label: '교육시간(교시)',
        required: true,
        editor: 'number',
        validate: requiredBy(isNonNegNumber, '교육시간은 0 이상의 숫자여야 합니다.'),
    },
    numberOfSession: {
        label: '교육횟수(회)',
        editor: 'number',
        validate: optional(isNonNegNumber, '교육횟수는 0 이상의 숫자여야 합니다.'),
    },
    /* 수강인원·수료인원은 서로 비교하지 않는다.
     * 한때 '수료인원 ≤ 수강인원' 을 걸었지만(엑셀에는 없던 규칙), 지역 확인 결과
     * 교육 신청은 하지 않았어도 실제 교육에 참여하는 경우가 있어
     * 수료인원이 수강인원보다 많은 것이 정상 데이터로 확인되었다. → 각자 0 이상만 본다. */
    numberOfApplicants: {
        label: '수강인원(명)',
        required: true,
        editor: 'number',
        validate: requiredBy(isNonNegNumber, '수강인원은 0 이상의 숫자여야 합니다.'),
    },
    numberOfGraduates: {
        label: '수료인원(명)',
        required: true,
        editor: 'number',
        validate: requiredBy(isNonNegNumber, '수료인원은 0 이상의 숫자여야 합니다.'),
    },
    startDate: {
        label: '교육 시작일',
        required: true,
        editor: 'text',
        validate: requiredBy(isYmdDash, '교육 시작일은 YYYY-MM-DD 형식의 실제 날짜여야 합니다.'),
    },
    endDate: {
        label: '교육 종료일',
        required: true,
        editor: 'text',
        // 엑셀에는 없지만 종료일이 시작일보다 앞서는 것은 명백한 오류라 함께 막는다.
        validate: (v, row) => {
            if (!isYmdDash(v)) return '교육 종료일은 YYYY-MM-DD 형식의 실제 날짜여야 합니다.'
            const s = row?.startDate
            if (isYmdDash(s) && String(v) < String(s)) return '교육 종료일은 시작일보다 빠를 수 없습니다.'
            return ''
        },
    },
    registrationYear: {
        label: '활동연도',
        required: true,
        editor: 'select',
        options: PROGRAM_OPTIONS.registrationYear,
        // 그리드 값은 숫자(2026), 드롭다운은 문자열 — 양쪽 허용
        validate: v =>
            PROGRAM_OPTIONS.registrationYear.includes(String(v)) ? '' : '활동연도는 목록에서 선택하세요.',
    },

    /* --- 수요기관 --- */
    isUnderserved: {
        label: '소외여부',
        required: true,
        editor: 'boolean', // API 는 boolean, 엑셀은 Y/N
    },
    organizationType: {
        label: '구분',
        required: true,
        editor: 'select',
        options: PROGRAM_OPTIONS.organizationType,
        validate: inList(PROGRAM_OPTIONS.organizationType, '구분은 목록에서 선택하세요.'),
    },
    grade: {
        label: '학년',
        required: true,
        editor: 'select',
        options: PROGRAM_OPTIONS.grade,
        validate: inList(PROGRAM_OPTIONS.grade, '학년은 초등/중등/고등/혼합 중 선택하세요.'),
    },
    classroom: {
        label: '학년/반',
        editor: 'text',
        validate: () => '', // 엑셀에 유효성 규칙 없음
    },
    organizationName: {
        label: '기관명',
        required: true,
        editor: 'text',
        validate: requiredBy(isText2, '기관명은 2자 이상이어야 하며 숫자만으로는 입력할 수 없습니다.'),
    },
    address: {
        label: '주소',
        required: true,
        editor: 'text',
        validate: requiredBy(isText2, '주소는 2자 이상이어야 하며 숫자만으로는 입력할 수 없습니다.'),
    },

    /* --- 수요처 담당자 ---
     * 유효성 검사를 두지 않는다. 형식(2자 이상·전화번호·이메일)과 직위/직책 목록 제한을
     * 모두 없애기로 했다. validate 가 없으면 validateProgramField 가 항상 '' 를 돌려준다.
     * 직위/직책은 자유 입력이 필요해 드롭다운(select)도 텍스트로 바꿨다. */
    managerName: {
        label: '담당자 성명',
        editor: 'text',
    },
    managerPosition: {
        label: '직위/직책',
        editor: 'text',
    },
    managerTelNumber: {
        label: '대표번호',
        editor: 'text',
    },
    managerPhoneNumber: {
        label: '휴대전화',
        editor: 'text',
    },
    managerEmail: {
        label: '이메일',
        editor: 'text',
    },
    managerRemark: {
        label: '수요처 특이사항',
        editor: 'text',
    },
}

/** 단일 필드 검증 — '' 통과 / 문자열 = 에러메시지 */
export const validateProgramField = (key, value, row) => {
    const spec = PROGRAM_FIELD_SPEC[key]
    if (!spec || !spec.validate) return ''
    return spec.validate(value, row)
}

/**
 * 행 전체 검증 (새 행 등록 시 사용).
 * 인라인 수정은 changedFields(실제로 건드린 필드)만 검증하지만, 새로 추가한 행은
 * 사용자가 손대지 않은 필드도 기본값이 아닌 실제 값으로 채워졌는지 전부 확인해야 한다.
 * @returns { valid: boolean, errors: { [key]: message } }
 */
export const validateProgramRow = row => {
    const errors = {}
    Object.keys(PROGRAM_FIELD_SPEC).forEach(key => {
        const msg = validateProgramField(key, row?.[key], row)
        if (msg) errors[key] = msg
    })
    return { valid: Object.keys(errors).length === 0, errors }
}
